import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, CircularProgress, MenuItem, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import StyleConstants from '../../../core/styles.constants';
import {
  convertDrawing,
  inspectDrawing,
  type DrawingConversion,
  type DrawingImportError,
  type DrawingInspection,
  type DrawingLayerTarget,
} from '../services/drawing-import.service';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface Props {
  readonly officeId: string;
  /** Hands the converted layers back, for the upload the administrator then confirms. */
  readonly onConverted: (conversion: DrawingConversion) => void;
}

const TARGETS: readonly DrawingLayerTarget[] = ['Ignore', 'Border', 'Floor', 'Room', 'Workplaces', 'Label'];

/**
 * Importing a CAD drawing, in the two steps it actually takes.
 *
 * First the file is described — its layers, what is on each, the units it declares and how
 * big that makes the building. Then the administrator says which layer is the floor, which
 * are the rooms and which holds the desks, and it is converted.
 *
 * The mapping is never guessed. Layer names in real drawings (`01-buitenwanden`, `A-WALL`,
 * `MEUBILAIR`) carry no convention a machine can rely on, and the cost of guessing wrong is
 * a floorplan whose furniture is its walls — which looks plausible until somebody tries to
 * book a desk.
 */
const DrawingImport = ({ officeId, onConverted }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [file, setFile] = useState<File | undefined>();
  const [inspection, setInspection] = useState<DrawingInspection | undefined>();
  const [mapping, setMapping] = useState<Record<string, DrawingLayerTarget>>({});
  const [scaleOverride, setScaleOverride] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DrawingImportError | undefined>();
  const [warnings, setWarnings] = useState<readonly string[]>([]);

  const inspect = async (chosen: File): Promise<void> => {
    setBusy(true);
    setWarnings([]);
    const result = await inspectDrawing(officeId, chosen, keycloak);
    setBusy(false);

    if (result.error || !result.data) {
      setInspection(undefined);
      setError(result.error ?? 'failed');
      return;
    }

    setError(undefined);
    setInspection(result.data);
    setMapping({});
  };

  const convert = async (): Promise<void> => {
    if (!file) {
      return;
    }

    setBusy(true);
    const typed = Number(scaleOverride.replace(',', '.'));
    const result = await convertDrawing(
      officeId,
      file,
      mapping,
      keycloak,
      Number.isFinite(typed) && typed > 0 ? typed : undefined,
    );
    setBusy(false);

    if (result.error || !result.data) {
      setError(result.error ?? 'failed');
      return;
    }

    setError(undefined);
    setWarnings(result.data.warnings);
    onConverted(result.data);
  };

  // Nothing mapped means nothing imported, so the button would produce an empty floorplan.
  const hasMapping = Object.values(mapping).some((target) => target !== 'Ignore');

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{t('floor-plan-import-cad')}</Typography>
      <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
        {t('floor-plan-import-cad-hint')}
      </Typography>

      <input
        type="file"
        accept=".dxf"
        onChange={(event): void => {
          const chosen = event.target.files?.[0];

          if (chosen) {
            setFile(chosen);
            inspect(chosen).catch(() => setError('failed'));
          }
        }}
      />

      {busy && <CircularProgress size={18} />}

      {error && (
        <Alert severity={error === 'forbidden' ? 'warning' : 'error'} sx={{ py: 0 }}>
          {t(`floor-plan-import-error-${error}`)}
        </Alert>
      )}

      {inspection && (
        <>
          <Typography variant="caption">
            {inspection.widthMetres
              ? t('floor-plan-import-scale-known', {
                units: inspection.unitsPerMetre,
                metres: inspection.widthMetres.toFixed(1),
              })
              : t('floor-plan-import-scale-unknown')}
          </Typography>

          {inspection.blockReferences > 0 && (
            <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
              {t('floor-plan-import-blocks', { count: inspection.blockReferences })}
            </Typography>
          )}

          {inspection.unresolvedBlockReferences > 0 && (
            // Named before the import rather than after it: the fix is to bind the xrefs and
            // export again, which happens in the CAD program.
            <Alert severity="warning" sx={{ py: 0 }}>
              {t('floor-plan-import-missing-blocks', {
                count: inspection.unresolvedBlockReferences,
                names: inspection.missingBlocks.slice(0, 3).join(', '),
              })}
            </Alert>
          )}

          <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('floor-plan-import-layer')}</TableCell>
                  <TableCell align="right">{t('floor-plan-import-contents')}</TableCell>
                  <TableCell>{t('floor-plan-import-target')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inspection.layers.map((layer) => (
                  <TableRow key={layer.name}>
                    <TableCell>
                      <Typography variant="body2">{layer.name}</Typography>
                      {layer.sampleText && (
                        <Typography variant="caption" color={StyleConstants.GREY_COLOR}>
                          {layer.sampleText}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {t('floor-plan-import-counts', {
                          closed: layer.closedPaths,
                          paths: layer.paths,
                          points: layer.points,
                          texts: layer.texts,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        value={mapping[layer.name] ?? 'Ignore'}
                        onChange={(event): void => setMapping((current) => ({
                          ...current,
                          [layer.name]: event.target.value as DrawingLayerTarget,
                        }))}
                        sx={{ minWidth: 130 }}
                      >
                        {TARGETS.map((target) => (
                          <MenuItem key={target} value={target}>
                            {t(`floor-plan-import-target-${target.toLowerCase()}`)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <TextField
            size="small"
            type="number"
            value={scaleOverride}
            onChange={(event): void => setScaleOverride(event.target.value)}
            label={String(t('floor-plan-import-units-override'))}
            helperText={String(t('floor-plan-import-units-override-hint'))}
          />

          <Button
            variant="contained"
            disabled={!hasMapping || busy}
            onClick={(): void => {
              convert().catch(() => setError('failed'));
            }}
          >
            {t('floor-plan-import-convert')}
          </Button>

          {warnings.map((warning) => (
            <Alert key={warning} severity="info" sx={{ py: 0 }}>
              {t(`floor-plan-import-warning-${warning}`)}
            </Alert>
          ))}
        </>
      )}
    </Stack>
  );
};

export default DrawingImport;
